import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";

const HorizontalCarousel = ({ children, interval = 1000 }) => {
  const containerRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [itemWidth, setItemWidth] = useState(0);
  
  const slides = React.Children.toArray(children);
  const slideCount = slides.length;

  const displayedSlides = useMemo(() => {
    if (slideCount === 0) return [];
    return [...slides, ...slides, ...slides];
  }, [slides, slideCount]);

  const calculateWidth = useCallback(() => {
    if (containerRef.current && containerRef.current.children.length > 0) {
      // Use getBoundingClientRect for sub-pixel accuracy to prevent "creeping" gaps
      const width = containerRef.current.children[0].getBoundingClientRect().width;
      setItemWidth(width);
      
      if (containerRef.current.scrollLeft === 0) {
        containerRef.current.scrollTo({
          left: slideCount * width,
          behavior: "instant",
        });
      }
    }
  }, [slideCount]);

  useEffect(() => {
    calculateWidth();
    // Tiny timeout ensures the DOM has rendered the widths correctly
    const timer = setTimeout(calculateWidth, 100);
    window.addEventListener("resize", calculateWidth);
    return () => {
      window.removeEventListener("resize", calculateWidth);
      clearTimeout(timer);
    };
  }, [calculateWidth, displayedSlides]);

  const handleInfiniteLoop = useCallback(() => {
    const container = containerRef.current;
    if (!container || itemWidth === 0) return;

    const scrollLeft = container.scrollLeft;
    const centerStart = slideCount * itemWidth;
    const centerEnd = 2 * slideCount * itemWidth;

    if (scrollLeft >= centerEnd) {
      container.scrollTo({ left: scrollLeft - centerStart, behavior: "instant" });
    }
    else if (scrollLeft <= centerStart - itemWidth) {
      container.scrollTo({ left: scrollLeft + centerStart, behavior: "instant" });
    }
  }, [itemWidth, slideCount]);

  const scrollNext = () => {
    if (!containerRef.current) return;
    containerRef.current.scrollBy({ left: itemWidth, behavior: "smooth" });
  };

  const scrollPrev = () => {
    if (!containerRef.current) return;
    containerRef.current.scrollBy({ left: -itemWidth, behavior: "smooth" });
  };

  useEffect(() => {
    if (paused || itemWidth === 0) return;
    const timer = setInterval(scrollNext, interval);
    return () => clearInterval(timer);
  }, [paused, interval, itemWidth]);

  return (
    <div
      className="relative group w-full mx-auto overflow-hidden" // Changed to overflow-hidden
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-[20] text-white bg-blue-600 w-10 h-10 flex items-center justify-center rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-blue-700"
      >
        ←
      </button>

      <button
        onClick={scrollNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-[20] text-white bg-blue-600 w-10 h-10 flex items-center justify-center rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-blue-700"
      >
        →
      </button>

      {/* Carousel Container */}
      <div
        ref={containerRef}
        onScroll={handleInfiniteLoop}
        className="flex overflow-x-auto scroll-smooth no-scrollbar"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {displayedSlides.map((child, index) => (
          <div 
            key={`slide-${index}`} 
            // Control sizes here: 1 card on mobile, 2 on tablet, 3 on desktop
            // px-3 creates the visual gap between cards
            className="flex-shrink-0 w-full sm:w-1/2 lg:w-1/3 px-3"
            onMouseEnter={() => setPaused(true)}
          >
            {child}
          </div>
        ))}
      </div>

      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default HorizontalCarousel;